/* eslint-disable max-lines */
import React, {
    useEffect, useRef, useState
} from 'react';
import {
    useParams, useSearchParams
} from 'react-router-dom';
import {
    Code, ChevronLeft, X, FlaskConical
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    cn
} from 'shared/lib/utils';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import InlineOpcodeEditor from '../opcode-editor/InlineOpcodeEditor';
import {
    BLANK_OPCODE_SEED
} from '../opcode-editor/templates';
import OpcodeTestPanel from '../components/OpcodeTestPanel';
import DSResourceList, { PAGE_SIZE_LIST } from './DSResourceList';

/**
 * DSOpCodesList — DS-themed list for Library → OpCodes.
 */
export default function DSOpCodesList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        oragID, subOragID
    } = useParams();
    const editingOpcodeId = searchParams.get('edit');
    const editorRef = useRef(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [search, setSearch] = useState('');
    const [creating, setCreating] = useState(false);
    const [editorDirty, setEditorDirty] = useState(false);
    const [showTest, setShowTest] = useState(true);
    const [opcodeTestRefresh, setOpcodeTestRefresh] = useState(0);

    const editingRow = items.find((it) => it.opcode_id === editingOpcodeId);

    function fetchPage(page) {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        const searching = search.length > 0;
        jsonOpcodeService.listJSONOpcodes({
            organizationId: oragID,
            subOrganizationId: subOragID,
            page: searching ? 1 : page,
            limit: searching ? 1000 : PAGE_SIZE_LIST,
        })
            .then((res) => {
                const payload = res?.data ?? res;
                if (payload && typeof payload === 'object' && 'data' in payload && 'pagination' in payload) {
                    setItems(Array.isArray(payload.data) ? payload.data : []);
                    setTotalPages(payload.pagination.totalPages ?? 1);
                    setTotalItems(payload.pagination.totalItems ?? payload.data.length);
                } else {
                    const list = Array.isArray(payload) ? payload : [];
                    setItems(list);
                    setTotalPages(1);
                    setTotalItems(list.length);
                }
            })
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }

    function reload() { fetchPage(currentPage); }
    useEffect(() => { setCurrentPage(1); }, [oragID, subOragID]);
    useEffect(() => { fetchPage(currentPage); }, [oragID, subOragID, currentPage, search]);

    function openEditor(o) {
        if (!o?.opcode_id) return;
        setSearchParams({ tab: 'opcodes', edit: o.opcode_id });
    }

    function closeStandaloneEditor() {
        setSearchParams({ tab: 'opcodes' }, { replace: true });
        setEditorDirty(false);
        reload();
    }

    async function createBlankOpcode() {
        if (creating) return;
        setCreating(true);
        const seed = {
            ...BLANK_OPCODE_SEED,
            title: items.length ? `New OpCode ${items.length + 1}` : 'New OpCode'
        };
        try {
            const res = await jsonOpcodeService.createJSONOpcode({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcode: seed
            });
            const newId = res?.data?.opcode_id || res?.opcode_id;
            if (newId) setSearchParams({ tab: 'opcodes', edit: newId });
        } catch (err) {
            alert('Could not create new opcode: ' + (err?.response?.data?.message || err?.message || String(err)));
        } finally {
            setCreating(false);
        }
    }

    async function handleDelete(o) {
        if (!window.confirm(`Delete OpCode "${o.title || o.opcode_id}"?\n\nAny assistant that references it will break until you re-link.`)) return;
        try {
            await jsonOpcodeService.deleteJSONOpcodes({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcodeIds: [o.opcode_id]
            });
            reload();
        } catch (err) {
            alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        }
    }

    async function saveBeforeTest() {
        const result = await editorRef.current?.flushSave?.();
        return result?.ok;
    }

    function handleOpcodeSaved() {
        setOpcodeTestRefresh((k) => k + 1);
    }

    return (
        <>
        <DSResourceList
            items={items}
            loading={loading}
            error={error}
            getKey={(it) => it.opcode_id}
            getCard={(it) => {
                const stepsCount = Array.isArray(it.steps) ? it.steps.length : 0;
                const kinds = Array.isArray(it.steps) ? [...new Set(it.steps.map((s) => s?.kind).filter(Boolean))] : [];
                const stepsLabel = stepsCount === 0
                    ? 'No steps'
                    : `${stepsCount} ${stepsCount === 1 ? 'step' : 'steps'}${kinds.length ? ` · ${kinds.join(' · ')}` : ''}`;
                return {
                    icon: Code,
                    tone: 'violet',
                    title: it.title || it.opcode_id,
                    description: it.description || '',
                    stepsLabel,
                    idLine: it.opcode_id,
                    createdBy: it.createdBy || it.audit?.createdBy || null,
                    createdAt: it.created || it.audit?.created || null,
                    searchTerms: [it.title, it.description, it.opcode_id, ...kinds]
                };
            }}
            showStepsColumn
            onPick={openEditor}
            onEdit={openEditor}
            onDelete={handleDelete}
            onCreate={createBlankOpcode}
            creating={creating}
            newLabel="New OpCode"
            searchPlaceholder="Search opcodes by title, kind, or id…"
            emptyTitle="No opcodes yet"
            emptyDescription="Create one from scratch — opcodes are the procedure your assistants run."
            emptyIcon={Code}
            serverPage={search ? undefined : currentPage}
            serverTotalPages={search ? undefined : totalPages}
            onServerPage={search ? undefined : setCurrentPage}
            serverItemCount={search ? undefined : totalItems}
            onSearchChange={(q) => { setSearch(q); setCurrentPage(1); }}
        />

        {editingOpcodeId && (
            <div
                className="fixed inset-0 z-50 flex flex-col bg-background"
                style={{ top: 0, left: 0 }}
            >
                <header className="flex shrink-0 items-center justify-between gap-4 border-b bg-muted/30 px-4 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={closeStandaloneEditor}
                            className="h-8 shrink-0 px-2 text-muted-foreground hover:text-foreground"
                        >
                            <ChevronLeft className="mr-1 h-4 w-4" />
                            Back to Library
                        </Button>
                        <div className="hidden h-5 w-px shrink-0 bg-border sm:block" aria-hidden />
                        <div className="min-w-0">
                            <h1 className="truncate text-sm font-semibold text-foreground">Edit OpCode</h1>
                            <p className="truncate text-xs text-muted-foreground">
                                {editingRow?.title || editingOpcodeId}
                            </p>
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        {editorDirty && showTest && (
                            <span className="mr-1 hidden rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-900 sm:inline">
                                Unsaved
                            </span>
                        )}
                        <Button
                            type="button"
                            variant={showTest ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setShowTest((v) => !v)}
                            title={showTest ? 'Hide OpCode test panel' : 'Open OpCode test panel'}
                            className={cn('h-8', showTest && 'font-semibold')}
                        >
                            <FlaskConical className="mr-1.5 h-3.5 w-3.5" />
                            {showTest ? 'Hide OpCode test' : 'OpCode test'}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={closeStandaloneEditor}
                            className="h-8 w-8 p-0 text-muted-foreground"
                            title="Close"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </header>
                <div className="flex flex-1 overflow-hidden">
                    <div className="flex-1 overflow-auto p-4 md:p-6">
                        <InlineOpcodeEditor
                            ref={editorRef}
                            opcodeId={editingOpcodeId}
                            initialDoc={editingRow}
                            onPristineChange={(p) => setEditorDirty(!p)}
                            onSaved={handleOpcodeSaved}
                            onDeleted={closeStandaloneEditor}
                        />
                    </div>
                    {showTest && (
                        <aside className="flex w-full shrink-0 flex-col overflow-hidden border-l bg-card md:w-[min(440px,40%)]">
                            <div className="flex shrink-0 items-center justify-between border-b bg-muted/30 px-3 py-2">
                                <span className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                                    OpCode test
                                </span>
                                {editorDirty && (
                                    <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                                        Save before run
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 min-h-0 overflow-hidden">
                                <OpcodeTestPanel
                                    lockedOpcode={{
                                        opcode_id: editingOpcodeId,
                                        title: editingRow?.title
                                    }}
                                    isDirty={editorDirty}
                                    onSaveFirst={saveBeforeTest}
                                    maximized={false}
                                    refreshSignal={opcodeTestRefresh}
                                />
                            </div>
                        </aside>
                    )}
                </div>
            </div>
        )}
        </>
    );
}
